import { Form, Input, message, Select } from 'antd'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Attachments from './Attachments'
import CommentList from './comment/CommentList'
import StatusPill from '../../../../shared/StatusPill'

import {
    createSelectOptions,
    isValidArray,
    isValidObject,
} from '../../../../../utils/helpers'
import {
    apiAddResult,
    getAttachmentPreUrl,
    uploadAttachmentToAWS,
} from '../../../../../redux/testRun/testRun.service'
import {
    testRunState,
    testRunTestCaseDetail,
    toggleReportDetails,
} from '../../../../../redux/testRun/testRunList.slice'
import SkeletonResultForm from './SkeletonResultForm'
import styles from '../../../../../testrun.module.css'

const { TextArea } = Input

const STATUS_OPTIONS = ['Pending', 'Pass', 'Fail', 'Retest']

function ResultForm(props) {
    const {
        stateTestRun = false,
        setStateTestRun = () => { },
        handleTestCaseList = () => { },
        setSelectedRowKeys = () => { },
        reportId = '',
    } = props

    const dispatch = useDispatch()
    const [form] = Form.useForm()

    const {
        testRunTestCaseDetailLoading = false,
        testRunTestCaseDetail: testCaseDetailData = {},
        fetchReportToggle,
    } = useSelector(testRunState)

    const [fileList, setFileList] = useState([])
    const [attachmentError, setAttachmentError] = useState('')
    const [saveLoading, setSaveLoading] = useState(false)

    const isSingleEdit = stateTestRun?.editType === 'single'
    const selectedCount = stateTestRun?.selectedTCID?.length || 0

    const generateImageUrl = async (file) => {
        const { name = '', type = '' } = file ?? {}
        const formData = {
            data: [
                {
                    filename: name,
                    contentType: type || 'document',
                },
            ],
        }

        const result = await getAttachmentPreUrl(formData)
        const { success = false, data = {} } = result?.data || {}

        if (success) {
            const uploadURL = data?.[0]?.uploadUrl ?? ''
            const publicLink = data?.[0]?.publicLink ?? ''
            const response = await uploadAttachmentToAWS(file, uploadURL)

            if (response?.ok) {
                return publicLink
            }
            return ''
        }

        return ''
    }

    const handleClose = () => {
        setStateTestRun((prev) => ({ ...prev, drawerEdit: false }))
    }

    const handleSubmit = async (values) => {
        setSaveLoading(true)
        let imageUrls = []

        if (fileList.length !== 0) {
            const uploadedUrls = await Promise.all(
                fileList.map(async (file) => {
                    const fileName = file?.name || ''
                    try {
                        const url = await generateImageUrl(file?.originFileObj)
                        return url ? { url, fileName } : null
                    } catch {
                        return null
                    }
                })
            )
            imageUrls = uploadedUrls.filter(Boolean)
        }

        const finalUrls = isValidArray(imageUrls)
            ? imageUrls.map((item) => ({
                url: item?.url || '',
                fileName: item?.fileName || '',
            }))
            : []

        const formData = {
            id: reportId,
            data: {
                testCaseIds: stateTestRun?.selectedTCID || [],
                status: isValidObject(values?.status)
                    ? values?.status?.value
                    : values?.status,
                ...(values?.comment || isValidArray(finalUrls)
                    ? {
                        comment: {
                            ...(values?.comment
                                ? { description: values.comment || '' }
                                : {}),
                            ...(isValidArray(finalUrls)
                                ? { attachments: finalUrls }
                                : {}),
                        },
                    }
                    : {}),
            },
        }

        const resp = await apiAddResult(formData)

        if (resp?.data?.success) {
            message.success(
                resp?.data?.message || 'Test case submitted successfully!'
            )
            setStateTestRun({ ...stateTestRun, drawerEdit: false })
            dispatch(toggleReportDetails(!fetchReportToggle))
            setFileList([])
            setSelectedRowKeys([])
            handleTestCaseList()
            form.resetFields()
        } else {
            message.error(resp?.data?.message || 'Something went wrong!')
        }
        setSaveLoading(false)
    }

    const handleTestCaseDetails = () => {
        const payload = {
            reportId,
            testCaseId: stateTestRun?.selectedTCID?.[0] || '',
        }
        dispatch(testRunTestCaseDetail(payload)).then((resp) => {
            if (isValidObject(resp?.payload?.data)) {
                const detailsData = resp?.payload?.data || {}
                form.setFieldsValue({
                    status: createSelectOptions(detailsData?.status),
                })
            }
        })
    }

    useEffect(() => {
        if (stateTestRun.drawerEdit && isSingleEdit) {
            handleTestCaseDetails()
            setFileList([])
            setAttachmentError('')
        }
    }, [stateTestRun.drawerEdit, isSingleEdit])

    const showFormSkeleton =
        isSingleEdit && testRunTestCaseDetailLoading

    return (
        <div className={styles.resultDrawerLayout}>
            <div className={styles.resultDrawerScroll}>
                {isSingleEdit && !showFormSkeleton && (
                    <div className={styles.resultCaseBanner}>
                        <div className={styles.resultCaseBannerTop}>
                            <span className={styles.resultCaseId}>
                                {testCaseDetailData?.testId || '—'}
                            </span>
                            <StatusPill status={testCaseDetailData?.status} />
                        </div>
                        <p className={styles.resultCaseTitle}>
                            {testCaseDetailData?.title || 'Test case'}
                        </p>
                    </div>
                )}

                {!isSingleEdit && selectedCount > 0 && (
                    <div className={styles.resultBulkBanner}>
                        <span className={styles.resultBulkCount}>
                            {selectedCount}
                        </span>
                        <div>
                            <p className={styles.resultBulkTitle}>
                                Bulk update
                            </p>
                            <p className={styles.resultBulkHint}>
                                Status and comment will apply to all selected
                                test cases.
                            </p>
                        </div>
                    </div>
                )}

                <section className={styles.resultFormCard}>
                    {showFormSkeleton ? (
                        <SkeletonResultForm />
                    ) : (
                        <Form
                            id="add-result-form"
                            requiredMark={false}
                            form={form}
                            layout="vertical"
                            className={styles.resultForm}
                            onFinish={handleSubmit}
                            initialValues={{
                                comment: '',
                                status: {
                                    label: 'Pending',
                                    value: 'Pending',
                                },
                            }}
                        >
                            <p className={styles.resultFormEyebrow}>
                                New result
                            </p>
                            <h3 className={styles.resultFormHeading}>
                                Record outcome
                            </h3>

                            <Form.Item
                                label="Status"
                                name="status"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select a status',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Select status"
                                    className={styles.resultStatusSelect}
                                    options={STATUS_OPTIONS.map((s) => ({
                                        value: s,
                                        label: s,
                                    }))}
                                    optionRender={(option) => (
                                        <span className={styles.resultStatusOption}>
                                            <StatusPill status={option.value} />
                                        </span>
                                    )}
                                    labelRender={(option) => (
                                        <span className={styles.resultStatusSelected}>
                                            <StatusPill
                                                status={
                                                    option?.value || option?.label
                                                }
                                            />
                                        </span>
                                    )}
                                />
                            </Form.Item>

                            <Form.Item label="Comment" name="comment">
                                <TextArea
                                    autoSize={{ minRows: 3, maxRows: 10 }}
                                    placeholder="Add notes about this run…"
                                    className={styles.resultCommentInput}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Attachments"
                                className={styles.resultAttachmentsField}
                                validateStatus={
                                    attachmentError ? 'error' : undefined
                                }
                                help={null}
                            >
                                <Attachments
                                    fileList={fileList}
                                    setFileList={setFileList}
                                    errorMessage={attachmentError}
                                    setErrorMessage={setAttachmentError}
                                />
                            </Form.Item>
                        </Form>
                    )}
                </section>

                {isSingleEdit && (
                    <section className={styles.commentHistorySection}>
                        <CommentList />
                    </section>
                )}
            </div>

            <div className={styles.resultDrawerFooter}>
                <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={handleClose}
                    disabled={saveLoading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    form="add-result-form"
                    className={`${styles.submitButton} ${saveLoading ? styles.submitButtonDisabled : ''
                        }`}
                    disabled={saveLoading || showFormSkeleton}
                >
                    {saveLoading ? 'Saving…' : 'Save result'}
                </button>
            </div>
        </div>
    )
}

export default ResultForm
